import { IntegrityMonitorRulesGridView } from '../view/IntegrityMonitorRulesGridView';
import { templateIntegrityMonitorRulesGrid } from '../view/template';
import { IntegrityMonitorRulesGridRepository } from '../repository/IntegrityMonitorRulesGridRepository';
import { BaseWebComponentController, WebComponentEvent } from '../../../../framework/BaseWebComponentController';
import { IntegrityMonitorRulesGridWebComponent } from '../index';
import {
  GridActions,
  DISPLAYED_COLUMNS_RULES,
  IM_RULES_DISPLAYED_FILTERS,
  IntegrityMonitorRulesGridWebComponentSchema,
  IntegrityMonitorRuleGridItem,
  IntegrityMonitorConfigurationStatus,
} from '../types';
import { DefaultSmartViewId, ViewType } from '../../integrityMonitorRulesSmartViews/types';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../../../grid/gridView/types';
import { SelectEvents } from '../../../../ui/gzSelect/types';
import { GridEvents } from '../../../../grid/types/common';
import { showErrorToast, showSuccessToast } from '@nirvana/shared/common';

export class IntegrityMonitorRulesGridController extends BaseWebComponentController {
  public component: IntegrityMonitorRulesGridWebComponent;
  public dataRepository: IntegrityMonitorRulesGridRepository;
  public view: IntegrityMonitorRulesGridView;
  public viewConstructor: typeof IntegrityMonitorRulesGridView;
  public dataRepositoryConstructor: typeof IntegrityMonitorRulesGridRepository;

  private selectedItemsGridData: Array<IntegrityMonitorRuleGridItem>;

  constructor(component: IntegrityMonitorRulesGridWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: IntegrityMonitorRulesGridView,
      viewTemplate: templateIntegrityMonitorRulesGrid,
      dataRepositoryConstructor: IntegrityMonitorRulesGridRepository,
    });
  }

  computeEventListeners() {
    this.view.dom.$gridView.onDataLoadingFinishedWithSuccess = this.tryFetchRulesCountInRuleSet.bind(this);

    return [
      {
        element: this.view.dom.$actionsSelect,
        event: SelectEvents.SELECTION_CHANGED,
        callback: this.onActionSelected,
      },
      {
        element: this.view.dom.$gridView,
        event: GridEvents.SELECT_ROWS,
        callback: this.onRuleSelected,
      },
    ];
  }

  async getDataForView(): Promise<IntegrityMonitorRulesGridWebComponentSchema> {
    const currentView = this.component.currentView ?? {
      id: DefaultSmartViewId.ALL_RULES,
      name: 'All rules',
      type: ViewType.ALL_RULES,
      filters: IM_RULES_DISPLAYED_FILTERS,
      displayedColumns: DISPLAYED_COLUMNS_RULES,
      compactView: false,
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: DEFAULT_PAGE_NUMBER,
    };

    const data = await this.dataRepository.getDataForView(currentView.type);

    return {
      activeView: currentView,
      activeViewId: currentView.id,
      allColumnDetails: data.allColumnDetails,
      defaultViewProps: data.defaultViewProps,
      predefinedFiltersKeys: data.predefinedFiltersKeys,
      ruleIdsFilter: this.component.ruleIdsFilter,
    };
  }

  private async onActionSelected(ev: WebComponentEvent) {
    switch (ev.detail.selected) {
      case GridActions.NEW_RULE:
        this.component.dispatchEvent(
          new CustomEvent(GridActions.NEW_RULE, {
            detail: {
              ruleSetId: this.component.currentView.type === ViewType.RULE_SET ? this.component.currentView.id : null,
            },
          })
        );
        break;
      case GridActions.NEW_RULE_SET:
        this.component.dispatchEvent(
          new CustomEvent(GridActions.NEW_RULE_SET, {
            detail: {
              selectedItems: this.selectedItemsGridData,
            },
          })
        );
        break;
      case GridActions.ASSIGN_RULES_TO_RULE_SETS:
        this.component.dispatchEvent(
          new CustomEvent(GridActions.ASSIGN_RULES_TO_RULE_SETS, {
            detail: {
              selectedItems: this.selectedItemsGridData,
            },
          })
        );
        break;
      case GridActions.UNASSIGN_RULES_FROM_RULE_SETS:
        await this.unassignRulesFromRuleSet();
        break;
      case GridActions.DELETE_RULES:
        this.component.dispatchEvent(
          new CustomEvent(GridActions.DELETE_RULES, {
            detail: {
              selectedItems: this.selectedItemsGridData,
            },
          })
        );
        break;
    }
  }

  private onRuleSelected(ev: WebComponentEvent) {
    this.selectedItemsGridData = ev.detail.selectionStoreData;

    this.handleNewRuleActionOptionEnabling();
    this.handleNewRuleSetActionOptionEnabling();
    this.handleAssignRulesActionOptionEnabling();
    this.handleUnassignRulesActionOptionEnabling();
    this.handleDeleteRulesActionOptionEnabling();
  }

  private handleNewRuleActionOptionEnabling() {
    this.selectedItemsGridData.length > 0
      ? this.view.disableActionOption(GridActions.NEW_RULE)
      : this.view.enableActionOption(GridActions.NEW_RULE);
  }

  private handleNewRuleSetActionOptionEnabling() {
    this.handleActionOptionUsingSelectedUnassignableRules(GridActions.NEW_RULE_SET);
  }

  private handleAssignRulesActionOptionEnabling() {
    this.handleActionOptionUsingSelectedUnassignableRules(GridActions.ASSIGN_RULES_TO_RULE_SETS);
  }

  private handleUnassignRulesActionOptionEnabling() {
    const selectedRulesCount = this.selectedItemsGridData.length;

    selectedRulesCount === 0 || selectedRulesCount === this.component.rulesCountInRuleSet
      ? this.view.disableActionOption(GridActions.UNASSIGN_RULES_FROM_RULE_SETS)
      : this.view.enableActionOption(GridActions.UNASSIGN_RULES_FROM_RULE_SETS);
  }

  private handleDeleteRulesActionOptionEnabling() {
    this.handleActionOptionUsingDefaultRules(GridActions.DELETE_RULES);
  }

  private handleActionOptionUsingSelectedUnassignableRules(optionValue: GridActions) {
    if (this.selectedItemsGridData.length === 0) {
      this.view.disableActionOption(optionValue);
      return;
    }

    const hasSelectedUnassignableRules = this.selectedItemsGridData.some(
      rule => rule.configurationStatus !== IntegrityMonitorConfigurationStatus.STATUS_CONFIGURED_OK
    );

    if (hasSelectedUnassignableRules) {
      this.view.disableActionOption(optionValue);
      return;
    }

    this.view.enableActionOption(optionValue);
  }

  private handleActionOptionUsingDefaultRules(optionValue: GridActions) {
    if (this.selectedItemsGridData.length === 0) {
      this.view.disableActionOption(optionValue);
      return;
    }

    const hasSelectedDefaultRules = this.selectedItemsGridData.some(rule => rule.isDefaultRule);

    if (hasSelectedDefaultRules) {
      this.view.disableActionOption(optionValue);
      return;
    }

    this.view.enableActionOption(optionValue);
  }

  private async unassignRulesFromRuleSet() {
    this.view.setLoadingState();

    const selectedRuleIds = this.selectedItemsGridData.map(rule => rule._id);
    const ruleSetId = this.component.currentView.id;

    try {
      await this.dataRepository.unassignRulesFromRuleSet(selectedRuleIds, ruleSetId);

      showSuccessToast(this.dependencies.translateFn('Rules unassigned successfully.'));

      // Rerender the grid for data reloading
      this.component.canFetchRulesCountInRuleSet = true;
      this.component.refreshGrid();
    } catch (error) {
      // if the exception is AuthenticationSecurityException it is already treated by the general session exception
      if (error?.exceptionType === 'Exceptions\\AuthenticationSecurityException') {
        return;
      }

      showErrorToast(error);
    } finally {
      this.view.removeLoadingState();
    }
  }

  private async tryFetchRulesCountInRuleSet() {
    if (this.component.currentView.type !== ViewType.RULE_SET) {
      return;
    }

    if (!this.component.canFetchRulesCountInRuleSet) {
      return;
    }

    this.component.rulesCountInRuleSet = await this.dataRepository.getRulesCountForRuleSet(
      this.component.currentView.id
    );

    this.component.canFetchRulesCountInRuleSet = false;
  }
}
